/**
 * ConfirmEmailChangePage — consume an email-change token and apply the change.
 *
 * URL: /confirm-email-change?token={token}
 *
 * This page is PUBLIC — no auth required. The user clicks the link from their
 * new-address inbox (possibly while logged out).
 *
 * Three states:
 *  1. Confirming — auto-calls confirmEmailChange(token) on mount; shows spinner
 *  2. Success    — email changed, all sessions invalidated by backend; user must
 *                  re-login. Clears local auth state and redirects to /login.
 *  3. Error      — invalid_or_expired_token or email_taken; shows error screen.
 *
 * Visual shell mirrors LoginPage / ResetPasswordPage for a coherent auth
 * experience (split-screen, brand-glow, surface-raised-2 right panel).
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { confirmEmailChange, clearTokens } from '@/services/authApi';
import { getErrorCode } from '@/utils/errors';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ConfirmingScreen() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        paddingTop: '40px',
      }}
    >
      <div
        style={{
          width: '32px',
          height: '32px',
          border: '3px solid var(--color-border-subtle)',
          borderTopColor: 'var(--color-brand-primary)',
          borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
        }}
      />
      <p className="text-body-sm" style={{ color: 'var(--color-text-muted)' }}>
        Confirming your new email address&hellip;
      </p>
    </div>
  );
}

function SuccessScreen() {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          navigate('/login', { replace: true });
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: 'rgba(34, 197, 94, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
        }}
      >
        <CheckCircleIcon
          style={{ width: '28px', height: '28px', color: '#22c55e' }}
        />
      </div>
      <h2
        className="text-display-lg"
        style={{
          color: 'var(--color-text-primary)',
          margin: '0 0 12px',
          letterSpacing: '-0.5px',
        }}
      >
        Email updated!
      </h2>
      <p
        className="text-body-md"
        style={{
          color: 'var(--color-text-secondary)',
          margin: '0 0 32px',
          lineHeight: 1.6,
        }}
      >
        Your email address has been updated. Please sign in again with your new
        address. Redirecting in {countdown} {countdown === 1 ? 'second' : 'seconds'}&hellip;
      </p>
      <Link
        to="/login"
        style={{
          display: 'inline-block',
          padding: '10px 24px',
          backgroundColor: 'var(--color-brand-primary)',
          color: 'var(--color-text-inverse)',
          borderRadius: '8px',
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: '14px',
        }}
      >
        Sign in now
      </Link>
    </div>
  );
}

function ErrorScreen({ errorCode }: { errorCode: string | undefined }) {
  const isExpired = errorCode === 'invalid_or_expired_token';
  const isTaken = errorCode === 'email_taken';

  const title = isTaken ? 'Email already taken' : 'Link invalid or expired';

  const body = isTaken
    ? 'Another account claimed that email address before you confirmed. Please request a new email change.'
    : 'This confirmation link is invalid or has expired. Please request a new email change from your profile.';

  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: 'rgba(248, 113, 113, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
        }}
      >
        <ExclamationTriangleIcon
          style={{ width: '28px', height: '28px', color: 'var(--color-signal-bad)' }}
        />
      </div>
      <h2
        className="text-display-lg"
        style={{
          color: 'var(--color-text-primary)',
          margin: '0 0 12px',
          letterSpacing: '-0.5px',
        }}
      >
        {title}
      </h2>
      <p
        className="text-body-md"
        style={{
          color: 'var(--color-text-secondary)',
          margin: '0 0 32px',
          lineHeight: 1.6,
        }}
      >
        {body}
      </p>
      {isExpired || isTaken ? (
        <Link
          to="/profile"
          style={{
            display: 'inline-block',
            padding: '10px 24px',
            backgroundColor: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '14px',
            marginBottom: '16px',
          }}
        >
          Go to Profile
        </Link>
      ) : null}
      <div style={{ marginTop: '12px' }}>
        <Link
          to="/login"
          style={{
            color: 'var(--color-text-muted)',
            textDecoration: 'none',
            fontSize: '13px',
          }}
        >
          Back to sign in
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function ConfirmEmailChangePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState<'confirming' | 'success' | 'error'>('confirming');
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);

  const didConfirm = useRef(false);

  useEffect(() => {
    if (didConfirm.current) return;
    didConfirm.current = true;

    if (!token) {
      setErrorCode('invalid_or_expired_token');
      setState('error');
      return;
    }

    confirmEmailChange(token)
      .then(() => {
        // Backend invalidated all sessions via token_version bump. Clear the
        // local access token so the user is treated as logged-out immediately.
        clearTokens();
        setState('success');
      })
      .catch((err: unknown) => {
        const code = getErrorCode(err);
        setErrorCode(code ?? 'invalid_or_expired_token');
        setState('error');
      });
  }, [token]);

  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Full-screen split layout — mirrors LoginPage / ResetPasswordPage */}
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          backgroundColor: 'var(--color-surface-base)',
        }}
      >
        {/* ── Left panel (desktop only) ─────────────────────────────────────── */}
        <div
          className="hidden lg:flex"
          style={{
            width: '50%',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'var(--color-surface-base)',
            padding: '64px 56px',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-80px',
              right: '-80px',
              width: '560px',
              height: '560px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0.06) 45%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: '-60px',
              left: '-40px',
              width: '360px',
              height: '360px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '28px',
              maxWidth: '380px',
            }}
          >
            <img
              src="/pwa-512.png"
              alt="PointeTracer logo"
              style={{
                width: '280px',
                height: '280px',
                objectFit: 'contain',
                filter:
                  'drop-shadow(0 0 24px rgba(59,130,246,0.45)) drop-shadow(0 8px 24px rgba(0,0,0,0.35))',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h1
                className="text-display-lg"
                style={{
                  color: 'var(--color-text-primary)',
                  margin: 0,
                  letterSpacing: '-0.5px',
                }}
              >
                Confirm new email
              </h1>
              <p
                className="text-body-md"
                style={{ color: 'var(--color-text-secondary)', margin: 0 }}
              >
                We are verifying your new email address. This only takes a moment.
              </p>
            </div>
          </div>
        </div>

        {/* ── Right panel (content) ─────────────────────────────────────────── */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 24px',
            backgroundColor: 'var(--color-surface-raised-2)',
          }}
          className="lg:w-1/2"
        >
          <div className="w-full" style={{ maxWidth: '400px' }}>
            {/* Mobile-only logo */}
            <div
              className="flex lg:hidden"
              style={{ justifyContent: 'center', marginBottom: '32px' }}
            >
              <img
                src="/pwa-512.png"
                alt="PointeTracer logo"
                style={{
                  width: '64px',
                  height: '64px',
                  objectFit: 'contain',
                  borderRadius: '16px',
                  boxShadow: '0 0 24px rgba(59,130,246,0.25)',
                }}
              />
            </div>

            {state === 'confirming' && <ConfirmingScreen />}
            {state === 'success' && <SuccessScreen />}
            {state === 'error' && <ErrorScreen errorCode={errorCode} />}
          </div>
        </div>
      </div>
    </>
  );
}
